// Note: Rule ignored because Immer.js enables us to do this safely
/* eslint no-param-reassign: 0 */

import moment from 'moment';
import produce from 'immer';
//
import {
  ADD_NEW_PRODUCT,
  CREATE_NEW_DEAL,
  DUPLICATE_DEAL,
  EDIT_DEAL_CANCEL,
  EDIT_DEAL_START,
  REMOVE_PRODUCT,
  SET_VALIDATION_ERRORS,
  TRIM_DEAL_FIELDS,
  UPDATE_DEAL_FIELD,
  UPDATE_DEAL_DIRTY_STATUS,
  UPDATE_PRODUCT_FIELD,
  UPDATE_NESTED_PRODUCT_FIELD,
} from 'store/deal-edit/types';
import { IDeal, IDealValidationErrors, FormulasInterface } from 'store/deals/types';
import { IDealProduct, IPrice } from 'store/field-options/types';
import { EVolumeUnit } from 'data/volume-units';
import { EStorageField, getCachedValue } from 'lib/storage';

const month = 11;

// Note: Converted simple object state to a function in order to
// support dynamic cached values
export const buildNewProductState = (): IDealProduct => ({
  name: getCachedValue(EStorageField.NAME),
  volumeUnit: getCachedValue(EStorageField.VOLUME_UNIT, EVolumeUnit.kton),
  volume: 1,
  endDate: moment()
    .add(1, 'year')
    .month(month)
    .toDate(),
  startDate: moment()
    .add(1, 'year')
    .month(0)
    .toDate(),
  basePrice: {
    description: '',
    formula: '',
    costFreight: 0,
    costReimbursed: 0,
    costFixed: 0,
    costTransitDays: 0,
    costIncInventory: 0,
  },
  mainBatna: {
    description: '',
    formula: '',
    costFreight: 0,
    costReimbursed: 0,
    costIncInventory: 0,
    costTransitDays: 0,
    costFixed: 0,
  },
  priceSwitch: null,
  transportMode: '',
  incoTerm: '',
  otherSupply: '',
});

// Note: Converted simple object state to a function in order to
// support dynamic cached values
export const buildInitialState = (): IDeal => ({
  id: '',
  createdOn: '',
  customer: '',
  name: '',
  status: '',
  description: '',
  region: getCachedValue(EStorageField.REGION),
  productLine: getCachedValue(EStorageField.PRODUCT_LINE),
  products: [buildNewProductState()],
  payCycle: null,
  payDelay: null,
  stdPayCycle: null,
  stdPayDelay: null,
  probDefault: null,
  isDirty: false,
  formulas: [],
  offerType: null,
  otherCredit: null,
  margin_at_risk: null,
  volume: null,
});

interface IActionAddNewProduct {
  type: typeof ADD_NEW_PRODUCT;
}

interface IActionCreateNewDeal {
  type: typeof CREATE_NEW_DEAL;
}

interface IActionDuplicateDeal {
  type: typeof DUPLICATE_DEAL;
  deal: IDeal;
}

interface IActionEditDealStart {
  type: typeof EDIT_DEAL_START;
  deal: IDeal;
}

interface IActionEditDealCancel {
  type: typeof EDIT_DEAL_CANCEL;
}

interface IActionRemoveProduct {
  type: typeof REMOVE_PRODUCT;
  index: number;
}

interface IActionTrimDealFields {
  type: typeof TRIM_DEAL_FIELDS;
}

interface IActionUpdateDealField {
  type: typeof UPDATE_DEAL_FIELD;
  field: string;
  value: string | number;
}

interface IActionUpdateDealDirtyStatus {
  type: typeof UPDATE_DEAL_DIRTY_STATUS;
  isDirty: boolean;
}

interface IActionUpdateProductField {
  type: typeof UPDATE_PRODUCT_FIELD;
  index: number;
  fieldName: string;
  value: string;
}

interface IActionUpdateNestedProductField {
  type: typeof UPDATE_NESTED_PRODUCT_FIELD;
  index: number;
  fieldName: string;
  nestedFieldName: string;
  value: string;
}

interface IActionSetValidationErrors {
  type: typeof SET_VALIDATION_ERRORS;
  errors: IDealValidationErrors;
}

type TAction =
  | IActionAddNewProduct
  | IActionCreateNewDeal
  | IActionDuplicateDeal
  | IActionUpdateProductField
  | IActionUpdateNestedProductField
  | IActionUpdateDealField
  | IActionEditDealStart
  | IActionEditDealCancel
  | IActionRemoveProduct
  | IActionSetValidationErrors
  | IActionTrimDealFields
  | IActionUpdateDealDirtyStatus;

const trimDealFieldsCustomer = (customer: string | undefined) => {
  return customer ? customer.trim() : '';
};

const trimDealFieldsName = (name: string) => {
  return name ? name.trim() : '';
};

const trimDealFieldsDescription = (description: string | undefined) => {
  return description ? description.trim() : '';
};

const getDealMapFormula = (formulas: FormulasInterface[] | undefined) => {
  return formulas?.map(df => ({ ...df, types: 'custom' }));
};

export default function(state = buildInitialState(), action: TAction) {
  switch (action.type) {
    case ADD_NEW_PRODUCT: {
      let newProducts;

      if (state.products) {
        newProducts = [...state.products, buildNewProductState()];
      } else {
        newProducts = [buildNewProductState()];
      }

      return {
        ...state,
        products: newProducts,
      };
    }

    case CREATE_NEW_DEAL: {
      return buildInitialState();
    }

    case DUPLICATE_DEAL: {
      const { deal } = action;
      return {
        ...deal,
        name: `${deal.name} Copy`,
      };
    }

    case UPDATE_DEAL_FIELD: {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case UPDATE_DEAL_DIRTY_STATUS: {
      return {
        ...state,
        isDirty: action.isDirty,
      };
    }

    case UPDATE_PRODUCT_FIELD: {
      return produce(state, (draftState: IDeal) => {
        if (!draftState.products) {
          return;
        }
        draftState.products[action.index][action.fieldName] = action.value;
      });
    }

    case UPDATE_NESTED_PRODUCT_FIELD: {
      return produce(state, (draftState: IDeal) => {
        if (!draftState.products) {
          return;
        }
        draftState.products[action.index][action.fieldName][action.nestedFieldName] = action.value;
      });
    }

    case EDIT_DEAL_START: {
      const { formulas } = action.deal;
      return {
        ...action.deal,
        formulas: getDealMapFormula(formulas),
      };
    }

    case EDIT_DEAL_CANCEL: {
      return buildInitialState();
    }

    case REMOVE_PRODUCT: {
      return produce(state, (draftState: IDeal) => {
        if (!draftState.products) {
          return;
        }
        draftState.products.splice(action.index, 1);
      });
    }

    case SET_VALIDATION_ERRORS: {
      return {
        ...state,
        validationErrors: action.errors,
      };
    }

    case TRIM_DEAL_FIELDS: {
      const { customer, name, description } = state;
      return {
        ...state,
        customer: trimDealFieldsCustomer(customer),
        name: trimDealFieldsName(name),
        description: trimDealFieldsDescription(description),
      };
    }

    default: {
      return state;
    }
  }
}

/**
 * Returns whether the current deal can be saved or not
 *
 * Deal must have:
 * - Customer Name
 * - Deal Name
 * - Contracting Season
 * - Start & End Date
 * - Region
 * - Product Line
 *
 * @param deal Deal state
 * @returns If deal can be saved or not
 */
export const canSaveDeal = (deal: IDeal): boolean => {
  const { customer, name, region, productLine } = deal;

  if (customer && name && region && productLine) {
    return true;
  }

  return false;
};

const checkDealNameVolumeBestPriceMainBatna = (
  name: string,
  volume: number | undefined,
  basePrice: IPrice | undefined,
  mainBatna: IPrice | undefined
) => {
  return name && volume && basePrice && mainBatna;
};

/**
 * Checks to see if a deal can be evaluated based on backend requirements
 *
 * Must have:
 * - At least one product
 *    - Each product must have all required fields populated
 *      - Product name
 *      - Unit
 *      - Start Date
 *      - End Date
 *      - Volume
 *      - Base/BATNA fields
 *
 * @param deal Deal state
 * @returns If deal can be evaluated or not
 */
export const canEvaluateDeal = (deal: IDeal): boolean => {
  const { products } = deal;

  if (!products?.length) {
    return false;
  }

  return products.every(product => {
    const { name, volume, startDate, endDate, basePrice, mainBatna } = product;
    return checkDealNameVolumeBestPriceMainBatna(name, volume, basePrice, mainBatna) && startDate && endDate;
  });
};
